import { Injectable, OnDestroy, inject } from '@angular/core';
import { GraphQLClient } from '../graphql/client';
import { ANALYTICS_MUTATIONS } from '../graphql/analytics-operations';
import { itemTypeToWire } from '../models/item';
import { environment } from '../../environments/environment';

/**
 * The lean ANONYMOUS analytics collector — used ONLY by the public surfaces
 * (portal shell, public collection view, public media lightbox, share view). Clones the member
 * collector's buffer/flush mechanics but deliberately differs:
 * - NO session minting and NO client sessionId — the server derives the daily-rotating session key
 *   from IP+UA (nothing is ever stored on the visitor's device, consistent with the no-banner posture);
 * - a per-gesture `clientEventId` (a replayed flush never double-counts);
 * - ONLY three factories: page views, lightbox previews, video plays. `collection.opened` /
 *   `article.opened` are SERVER producers (a client copy would double-count them);
 * - the unload beacon carries NO Authorization header (anonymous mutation).
 * Kept separate from the member AnalyticsCollectorService BY DESIGN — mixing auth modes in one
 * buffer risks anonymous events riding the member mutation after login (and vice versa).
 */
@Injectable({ providedIn: 'root' })
export class PublicAnalyticsCollectorService implements OnDestroy {
  private readonly graphql = inject(GraphQLClient);

  private queue: PublicAnalyticsEventInput[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private isFlushing = false;
  private retryCount = 0;

  private readonly QUEUE_LIMIT = 25;
  private readonly FLUSH_INTERVAL_MS = 30_000;
  private readonly MAX_RETRIES = 3;
  // Mirrors the server's AnalyticsEventLimits.MaxBatchSize (the mutation rejects larger batches).
  private readonly SERVER_MAX_BATCH = 100;

  private readonly boundVisibilityHandler = this.onVisibilityChange.bind(this);
  private readonly boundBeforeUnloadHandler = this.onBeforeUnload.bind(this);

  constructor() {
    this.flushTimer = setInterval(() => this.flush(), this.FLUSH_INTERVAL_MS);
    document.addEventListener('visibilitychange', this.boundVisibilityHandler);
    window.addEventListener('beforeunload', this.boundBeforeUnloadHandler);
  }

  // --- Public API: the THREE anonymous gestures (the server whitelist rejects everything else) ---

  /** Anonymous page/module view (fired by the app shell on anonymous routes). */
  pageViewed(module: string): void {
    this.enqueue('page.viewed', undefined, undefined, undefined, undefined, { module });
  }

  /** Anonymous lightbox open — "which assets draw anonymous eyes". The mounting surface stamps
   * itself into the payload so portal engagement is separable from share-link engagement. */
  itemPreviewed(itemId: string, itemType: number, containerId?: string, containerType?: number,
                payload?: { source: 'portal' | 'share_link' }): void {
    this.enqueue('item.previewed', itemId, itemType, containerId, containerType, payload);
  }

  /** Anonymous video play start in the public lightbox. */
  videoPlayed(itemId: string, durationSeconds?: number, quality?: string): void {
    this.enqueue('video.played', itemId, undefined, undefined, undefined, { durationSeconds, quality });
  }

  // --- Flush control (member-collector mechanics, sessionless) ---

  flush(): void {
    if (this.queue.length === 0 || this.isFlushing) return;

    const events = this.queue.slice(0, this.SERVER_MAX_BATCH);
    this.queue = this.queue.slice(this.SERVER_MAX_BATCH);
    this.isFlushing = true;

    this.graphql
      .mutate(ANALYTICS_MUTATIONS.RECORD_PUBLIC_EVENTS, { input: { events, context: this.sessionContext() } })
      .subscribe({
        next: () => {
          this.isFlushing = false;
          this.retryCount = 0;
        },
        error: () => {
          this.isFlushing = false;
          if (this.retryCount < this.MAX_RETRIES) {
            this.retryCount++;
            // Re-enqueue (prepend, order-preserving); clientEventId makes the retry replay-safe.
            this.queue = [...events, ...this.queue].slice(0, this.QUEUE_LIMIT * 2);
          }
          // After max retries events drop silently — analytics never blocks the public UX.
        },
      });
  }

  ngOnDestroy(): void {
    this.flush();
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
    window.removeEventListener('beforeunload', this.boundBeforeUnloadHandler);
  }

  // --- Internal ---

  private enqueue(
    type: string,
    itemId?: string,
    itemType?: number,
    containerId?: string,
    containerType?: number,
    payloadData?: Record<string, unknown>,
  ): void {
    this.queue.push({
      clientEventId: crypto.randomUUID(),
      type,
      timestamp: new Date().toISOString(),
      itemId: itemId ?? undefined,
      itemType: itemTypeToWire(itemType),
      containerId: containerId ?? undefined,
      containerType: itemTypeToWire(containerType),
      payload: payloadData ? JSON.stringify(payloadData) : undefined,
    });

    if (this.queue.length >= this.QUEUE_LIMIT) {
      this.flush();
    }
  }

  /** Session-row context (locale/viewport/timezone) — the server derives the session KEY itself. */
  private sessionContext(): PublicSessionContextInput {
    return {
      locale: navigator.language,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

  private onVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      this.flushViaSendBeacon();
    }
  }

  private onBeforeUnload(): void {
    this.flushViaSendBeacon();
  }

  /** keepalive fetch for tab close/hide — NO Authorization header (anonymous mutation). */
  private flushViaSendBeacon(): void {
    if (this.queue.length === 0) return;

    const events = [...this.queue];
    this.queue = [];

    const body = JSON.stringify({
      query: ANALYTICS_MUTATIONS.RECORD_PUBLIC_EVENTS,
      variables: { input: { events, context: this.sessionContext() } },
    });

    fetch(`${environment.apiUrl}/graphql`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* best-effort delivery on page unload */ });
  }
}

/** Wire shape of PublicAnalyticsEventInput (ItemType enum names via itemTypeToWire — no magic numbers). */
export interface PublicAnalyticsEventInput {
  clientEventId: string;
  type: string;
  timestamp: string;
  itemId?: string;
  itemType?: string;
  containerId?: string;
  containerType?: string;
  payload?: string;
}

export interface PublicSessionContextInput {
  locale?: string;
  viewport?: string;
  timezone?: string;
}
